/**
 * "More" menu — a trigger button and a small popover list of secondary actions.
 *
 * Added for the Studio redesign. The prototype put every action on screen as its
 * own icon: a song row carried five unlabelled glyphs and the Studio drawer
 * carried seven, which is what made both feel like a control panel rather than a
 * music product. Secondary actions live in here now; the primary action (Play)
 * stays visible.
 *
 * It is a menu, not a dialog: no portal, no overlay, no focus trap. That keeps it
 * usable inside the Studio drawer, which is itself a fixed layer.
 *
 * Behaviour it has to get right, because a bare dropdown gets all of it wrong:
 *   - Escape closes and returns focus to the trigger.
 *   - A click anywhere outside closes it.
 *   - Up/Down move between items, so it is operable without a mouse.
 *   - Inside a clickable row (see songs/SongCard) neither the trigger nor the
 *     items may bubble, or opening the menu would also open the Studio.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import Tooltip from './Tooltip';
import { c } from '../../styles/theme';

export interface ActionMenuItem {
  /** Visible text. Kept short — "Download", not "Download this song". */
  label: string;
  /**
   * Accessible name, when the visible label is too terse to identify the target.
   * Song rows use this to keep names song-scoped ("Download Midnight Drive").
   */
  ariaLabel?: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Renders in the danger colour, for destructive actions. */
  danger?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible name of the trigger. */
  label?: string;
  /**
   * The tooltip's text, when the accessible name is too long to hover over.
   *
   * `label` is song-scoped ("More actions for Midnight drive") because a page with
   * several of these needs each one distinguishable in a list of controls. That is the
   * right accessible name and the wrong bubble — a tooltip repeating the title of the
   * song it is sitting next to is noise. Defaults to "More actions".
   */
  tooltip?: string;
  /** Which edge the popover aligns to. */
  align?: 'left' | 'right';
  /**
   * Preferred side of the trigger the popover opens on.
   *
   * The menu will flip to the opposite side when the requested side would be clipped by
   * the viewport or by a scroll container. 'top' still matters for controls that live on
   * the workspace's bottom edge — it is the preferred side, not an absolute one.
   */
  placement?: 'bottom' | 'top';
  /** Stops clicks from reaching a clickable ancestor, e.g. a song row. */
  stopPropagation?: boolean;
  triggerStyle?: CSSProperties;
  style?: CSSProperties;
}

const ITEM: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  background: 'none',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'Inter',
  textAlign: 'left',
  color: c.text1,
};

/** True when an ancestor can clip overflow from the menu. */
function clipsOverflow(value: string) {
  return /(auto|scroll|hidden|clip)/.test(value);
}

/**
 * The nearest rectangle the menu has to fit inside.
 *
 * Usually that is the viewport. Inside the Studio workspace it is often the centre
 * column instead: each column is its own scroller, so a menu opening past that column's
 * bottom edge is clipped before z-index even gets a vote and looks as if it went behind
 * the docked transport. That is the bug the version-history row hits near the bottom.
 */
function boundaryRect(from: HTMLElement) {
  for (let node = from.parentElement; node; node = node.parentElement) {
    const s = window.getComputedStyle(node);
    if (clipsOverflow(s.overflow) || clipsOverflow(s.overflowX) || clipsOverflow(s.overflowY)) {
      return node.getBoundingClientRect();
    }
  }
  return {
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    left: 0,
  };
}

export default function ActionMenu({
  items,
  label = 'More actions',
  tooltip,
  align = 'right',
  placement = 'bottom',
  stopPropagation,
  triggerStyle,
  style,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  /**
   * Horizontal nudge, in pixels, that keeps the popover inside its visible bounds.
   *
   * Usually those bounds are the viewport. In the Studio workspace they are often the
   * centre column instead, because each column is its own scroller with overflow clipped.
   * The nudge is therefore measured against the nearest clipping ancestor rather than the
   * window alone.
   */
  const [shift, setShift] = useState(0);
  /**
   * The side the menu actually opens on after measurement.
   *
   * `placement` is the preference. Near the bottom edge of the Version History panel the
   * preferred bottom-opening menu lands under the docked transport and is clipped by the
   * centre column, so the usable answer is to open upward instead.
   */
  const [resolvedPlacement, setResolvedPlacement] = useState<'bottom' | 'top'>(placement);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Close on any click outside. mousedown rather than click so the menu is gone
  // before the click lands on whatever was pressed underneath.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus the first item on open, so the keyboard path starts inside the menu.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [open]);

  /*
    Pull the popover inside the visible box and, when needed, flip it to the safer side.

    The "visible box" is not always the viewport. In the workspace, each column is an
    overflowing scroller, and a menu that opens below a trigger near the bottom is cut
    off by that column before it can overlap the transport. That reads as "behind the
    player" even when its z-index is higher, because the pixels simply are not painted.

    So this effect does two things in one frame:
      - choose top vs bottom by comparing both fits against the nearest clipping ancestor
      - nudge left/right so the chosen menu stays inside the same ancestor

    The reset first makes repeated opens idempotent; otherwise a previous shift or flip
    would contaminate the next measurement. jsdom returns zero-sized rects, so the tests
    assert the written style branches rather than actual geometry.
  */
  useLayoutEffect(() => {
    if (!open) return;
    setShift(0);
    setResolvedPlacement(placement);

    const wrap = wrapRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;

    const anchorRect = (triggerRef.current ?? wrap).getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (listRect.width === 0 || listRect.height === 0) return;

    const bounds = boundaryRect(wrap);
    const EDGE = 8;
    const gap = 6;
    const spaceBelow = bounds.bottom - anchorRect.bottom - gap;
    const spaceAbove = anchorRect.top - bounds.top - gap;
    const fitsBelow = listRect.height <= spaceBelow;
    const fitsAbove = listRect.height <= spaceAbove;

    if (placement === 'bottom') {
      if (!fitsBelow && (fitsAbove || spaceAbove > spaceBelow)) setResolvedPlacement('top');
    } else if (!fitsAbove && (fitsBelow || spaceBelow > spaceAbove)) {
      setResolvedPlacement('bottom');
    }

    if (listRect.left < bounds.left + EDGE) setShift(bounds.left + EDGE - listRect.left);
    else if (listRect.right > bounds.right - EDGE) setShift(bounds.right - EDGE - listRect.right);
  }, [open, placement]);

  /** Moves focus between items, wrapping at both ends. */
  function move(from: HTMLElement, delta: number) {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []
    );
    const i = buttons.indexOf(from as HTMLButtonElement);
    const next = buttons[(i + delta + buttons.length) % buttons.length];
    next?.focus();
  }

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', ...style }}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          e.stopPropagation();
          close(true);
        }
      }}
    >
      {/*
        The trigger is three dots and nothing else, so it is one of the controls that
        most needs a bubble — but only while its own menu is shut, or the tooltip would
        cover the items it just revealed. Hence `disabled={open}`.

        `title` stays on the button as well. The two do not conflict: the browser's
        tooltip is suppressed while a styled one is showing anyway, and it remains the
        fallback in the contexts this menu renders in without one.
      */}
      <Tooltip label={tooltip ?? 'More actions'} disabled={open}>
        <button
          ref={triggerRef}
          type="button"
          aria-label={label}
          title={label}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 8,
            background: open ? c.surface3 : 'none',
            border: 'none',
            cursor: 'pointer',
            color: c.text2,
            ...triggerStyle,
          }}
        >
          <MoreHorizontal size={15} color={open ? c.text1 : c.text2} />
        </button>
      </Tooltip>

      {open && (
        <div
          ref={listRef}
          role="menu"
          aria-label={label}
          style={{
            position: 'absolute',
            ...(resolvedPlacement === 'top'
              ? { bottom: 'calc(100% + 6px)' }
              : { top: 'calc(100% + 6px)' }),
            [align]: 0,
            minWidth: 168,
            /* A floor of 168 and a ceiling of the viewport: minWidth alone wins over a
               narrower screen, so on a 360px phone with the menu near an edge the list
               would be sized past the edge before `shift` ever got to move it. */
            maxWidth: 'calc(100vw - 16px)',
            /* Applied on the axis the alignment does not own, so it composes with
               `[align]: 0` instead of fighting it. Zero on a desktop. */
            transform: shift ? `translateX(${shift}px)` : undefined,
            padding: 5,
            background: c.surface2,
            border: `1px solid ${c.borderStrong}`,
            borderRadius: 10,
            boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
            zIndex: 20,
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              aria-label={item.ariaLabel}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  move(e.currentTarget, 1);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  move(e.currentTarget, -1);
                }
              }}
              className="lyx-menu-item"
              style={{ ...ITEM, color: item.danger ? c.danger : c.text1 }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
